import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, X, ChevronRight, ChevronLeft, Maximize2, Ship, Truck, Car, Sparkles, Anchor, Caravan, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const VIDEOS = [
  {
    id: "vid-premium",
    titleKey: "video_showcase.vid_premium_title",
    descKey: "video_showcase.vid_premium_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/105lq97k_WashPros_Premium_Demo.mp4",
    poster: "https://images.unsplash.com/photo-1687675419180-5ac12bd3dfd5?w=800&q=80",
    icon: Sparkles,
    badgeKey: "video_showcase.badge_featured",
    color: "cyan",
  },
  {
    id: "vid-mobile",
    titleKey: "video_showcase.vid_mobile_title",
    descKey: "video_showcase.vid_mobile_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/9akhjcqd_WashPros_Mobile_Service.mp4",
    poster: "https://images.unsplash.com/photo-1638303429651-24e18372ce1f?w=800&q=80",
    icon: Car,
    badgeKey: "video_showcase.badge_popular",
    color: "emerald",
  },
  {
    id: "vid-boat",
    titleKey: "video_showcase.vid_boat_title",
    descKey: "video_showcase.vid_boat_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/hp5e9kt1_WashPros_Boat_Detailing.mp4",
    poster: "https://images.unsplash.com/photo-1700183235366-b43e17f663e9?w=800&q=80",
    icon: Ship,
    color: "blue",
  },
  {
    id: "vid-marine",
    titleKey: "video_showcase.vid_marine_title",
    descKey: "video_showcase.vid_marine_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/n9c5s4w4_WashPros_Marine_Services.mp4",
    poster: "https://images.unsplash.com/photo-1770700391793-22fc46041c2b?w=800&q=80",
    icon: Anchor,
    color: "sky",
  },
  {
    id: "vid-fleet",
    titleKey: "video_showcase.vid_fleet_title",
    descKey: "video_showcase.vid_fleet_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/u12kzwmt_WashPros_Fleet_Management.mp4",
    poster: "https://images.unsplash.com/photo-1761934657948-708146148588?w=800&q=80",
    icon: Truck,
    badgeKey: "video_showcase.badge_business",
    color: "amber",
  },
  {
    id: "vid-rv",
    titleKey: "video_showcase.vid_rv_title",
    descKey: "video_showcase.vid_rv_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/ae7rwufd_WashPros_RV_Detailing.mp4",
    poster: "https://images.unsplash.com/photo-1561361513-2d000a50f0dc?w=800&q=80",
    icon: Caravan,
    badgeKey: "video_showcase.badge_new",
    color: "rose",
  },
  {
    id: "vid-become-vendor",
    titleKey: "video_showcase.vid_vendor_title",
    descKey: "video_showcase.vid_vendor_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/5fggl61a_WashPros_StickFigure_Become_Vendor.mp4",
    poster: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80",
    icon: Users,
    badgeKey: "video_showcase.badge_join",
    color: "violet",
  },
  {
    id: "vid-big-truck",
    titleKey: "video_showcase.vid_truck_title",
    descKey: "video_showcase.vid_truck_desc",
    src: "https://customer-assets.emergentagent.com/job_e9151d65-471c-46ed-8d46-2b6546085e42/artifacts/kn0q73cy_WashPros_StickFigure_Big_Truck.mp4",
    poster: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80",
    icon: Truck,
    color: "orange",
  },
];

const BADGE_STYLES = {
  cyan: "bg-cyan-500",
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  sky: "bg-sky-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  violet: "bg-violet-500",
  orange: "bg-orange-500",
};

const RING_STYLES = {
  cyan: "ring-cyan-500/40",
  emerald: "ring-emerald-500/40",
  blue: "ring-blue-500/40",
  sky: "ring-sky-500/40",
  amber: "ring-amber-500/40",
  rose: "ring-rose-500/40",
  violet: "ring-violet-500/40",
  orange: "ring-orange-500/40",
};

const ICON_STYLES = {
  cyan: "text-cyan-400",
  emerald: "text-emerald-400",
  blue: "text-blue-400",
  sky: "text-sky-400",
  amber: "text-amber-400",
  rose: "text-rose-400",
  violet: "text-violet-400",
  orange: "text-orange-400",
};

const VideoCard = ({ video, onExpand, size = "normal" }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  const Icon = video.icon;

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!videoRef.current || videoUnavailable) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => { /* Autoplay may be blocked by browser policy */ });
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const isLarge = size === "large";

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden bg-slate-950 border border-white/[0.06] hover:border-white/20 transition-all duration-500 hover:shadow-xl hover:shadow-black/30 ${isLarge ? "row-span-2" : ""}`}
      data-testid={`video-card-${video.id}`}
    >
      <div className={`${isLarge ? "aspect-[4/5]" : "aspect-video"} relative overflow-hidden`}>
        {/* Poster image is always shown — guaranteed Unsplash CDN, never fails */}
        <img
          src={video.poster}
          alt={t(video.titleKey)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {!videoUnavailable && (
          <video
            ref={videoRef}
            src={video.src}
            poster={video.poster}
            muted={muted}
            loop
            playsInline
            preload="none"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={() => setVideoUnavailable(true)}
          />
        )}
        {/* Dark overlay - heavier at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30 pointer-events-none" />

        {/* Badge */}
        {video.badgeKey && (
          <div className={`absolute top-3 left-3 px-2.5 py-1 ${BADGE_STYLES[video.color]} rounded-full text-[11px] text-white font-bold tracking-wide shadow-lg`}>
            {t(video.badgeKey)}
          </div>
        )}

        {/* Center play button (when paused) */}
        {!playing && (
          <button
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
            data-testid={`play-${video.id}`}
          >
            <div className={`${isLarge ? "w-16 h-16" : "w-12 h-12"} rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center hover:bg-white/25 hover:scale-110 transition-all duration-300 ring-2 ${RING_STYLES[video.color]}`}>
              <Play className={`${isLarge ? "w-7 h-7" : "w-5 h-5"} text-white ml-0.5`} fill="white" />
            </div>
          </button>
        )}

        {/* Bottom info bar + controls */}
        <div className={`absolute bottom-0 left-0 right-0 p-3 ${isLarge ? "p-4" : "p-3"} flex items-end justify-between transition-opacity duration-300 ${playing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} z-10`}>
          <div className="flex-1 min-w-0 mr-3">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Icon className={`w-3.5 h-3.5 ${ICON_STYLES[video.color]} shrink-0`} />
              <h3 className={`text-white font-semibold ${isLarge ? "text-base" : "text-sm"} truncate drop-shadow-md`}>{t(video.titleKey)}</h3>
            </div>
            <p className="text-slate-300/80 text-xs truncate drop-shadow">{t(video.descKey)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {playing && (
              <button onClick={togglePlay} className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-colors" data-testid={`pause-${video.id}`}>
                <Pause className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={toggleMute} className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-colors" data-testid={`mute-${video.id}`}>
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            <button onClick={(e) => { e.stopPropagation(); onExpand(video); }} className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-colors" data-testid={`expand-${video.id}`}>
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Title overlay always visible at bottom when not hovering */}
        <div className={`absolute bottom-0 left-0 right-0 ${isLarge ? "p-4" : "p-3"} transition-opacity duration-300 ${playing ? 'opacity-0' : 'group-hover:opacity-0'} z-[5]`}>
          <div className="flex items-center gap-1.5 mb-0.5">
            <Icon className={`w-3.5 h-3.5 ${ICON_STYLES[video.color]} shrink-0`} />
            <h3 className={`text-white font-semibold ${isLarge ? "text-base" : "text-sm"} truncate drop-shadow-md`}>{t(video.titleKey)}</h3>
          </div>
          <p className="text-slate-300/80 text-xs truncate drop-shadow">{t(video.descKey)}</p>
        </div>
      </div>
    </div>
  );
};

const VideoShowcaseSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(null);
  const fullscreenRef = useRef(null);
  const scrollRef = useRef(null);

  const openFullscreen = (video) => setFullscreen(video);
  const closeFullscreen = () => {
    if (fullscreenRef.current) fullscreenRef.current.pause();
    setFullscreen(null);
  };

  // Handle keyboard navigation in fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e) => {
      if (e.key === "Escape") closeFullscreen();
      if (e.key === "ArrowRight") {
        const idx = VIDEOS.findIndex(v => v.id === fullscreen.id);
        setFullscreen(VIDEOS[(idx + 1) % VIDEOS.length]);
      }
      if (e.key === "ArrowLeft") {
        const idx = VIDEOS.findIndex(v => v.id === fullscreen.id);
        setFullscreen(VIDEOS[(idx - 1 + VIDEOS.length) % VIDEOS.length]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreen]);

  // Row 1: Featured (large) + 2 stacked
  // Row 2: 3 across
  // Row 3: 2 + something else
  const featured = VIDEOS[0];
  const topRight = VIDEOS.slice(1, 3);
  const midRow = VIDEOS.slice(3, 6);
  const bottomRow = VIDEOS.slice(6, 8);

  return (
    <>
      <section className="relative py-16 sm:py-24 overflow-hidden" data-testid="video-showcase-section">
        {/* Subtle background accents */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/[0.03] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/[0.03] rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] mb-5">
              <Play className="w-3.5 h-3.5 text-cyan-400" fill="currentColor" />
              <span className="text-cyan-400 text-xs font-semibold uppercase tracking-widest">{t("video_showcase.header_badge")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 tracking-tight">
              {t("video_showcase.header_title")}
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto text-sm sm:text-base">
              {t("video_showcase.header_sub")}
            </p>
          </div>

          {/* ===== DESKTOP GRID (lg+) ===== */}
          <div className="hidden lg:block space-y-4">
            {/* Row 1: Featured large + 2 stacked */}
            <div className="grid grid-cols-3 gap-4">
              <VideoCard video={featured} onExpand={openFullscreen} size="large" />
              <div className="col-span-2 grid grid-cols-2 gap-4">
                {topRight.map((v) => (
                  <VideoCard key={v.id} video={v} onExpand={openFullscreen} />
                ))}
                {/* Fill with first of mid row to balance */}
                {midRow.slice(0, 2).map((v) => (
                  <VideoCard key={v.id} video={v} onExpand={openFullscreen} />
                ))}
              </div>
            </div>
            {/* Row 2: Remaining 3 */}
            <div className="grid grid-cols-3 gap-4">
              {midRow.slice(2).map((v) => (
                <VideoCard key={v.id} video={v} onExpand={openFullscreen} />
              ))}
              {bottomRow.map((v) => (
                <VideoCard key={v.id} video={v} onExpand={openFullscreen} />
              ))}
            </div>
          </div>

          {/* ===== TABLET GRID (md) ===== */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-4">
            {VIDEOS.map((v) => (
              <VideoCard key={v.id} video={v} onExpand={openFullscreen} />
            ))}
          </div>

          {/* ===== MOBILE: Horizontal scroll ===== */}
          <div className="md:hidden">
            <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              {VIDEOS.map((v) => (
                <div key={v.id} className="shrink-0 w-[80vw] snap-center">
                  <VideoCard video={v} onExpand={openFullscreen} />
                </div>
              ))}
            </div>
            {/* Scroll dots */}
            <div className="flex justify-center gap-1.5 mt-3">
              {VIDEOS.map((v, i) => (
                <div key={v.id} className="w-1.5 h-1.5 rounded-full bg-white/20" />
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-12">
            <Button
              onClick={() => navigate("/book/simple")}
              className="bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-full px-8 h-12 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-400/30 transition-all"
              data-testid="book-wash-cta"
            >
              {t("video_showcase.cta_book")}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/vendor/apply")}
              className="border-white/10 text-slate-400 hover:text-white hover:bg-white/5 rounded-full px-8 h-12"
              data-testid="become-vendor-cta"
            >
              <Users className="w-4 h-4 mr-2" />
              {t("video_showcase.cta_become")}
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FULLSCREEN PLAYER ===== */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          data-testid="video-fullscreen"
          onClick={closeFullscreen}
        >
          {/* Close */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            data-testid="close-fullscreen"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Prev */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const idx = VIDEOS.findIndex(v => v.id === fullscreen.id);
              setFullscreen(VIDEOS[(idx - 1 + VIDEOS.length) % VIDEOS.length]);
            }}
            className="absolute left-3 sm:left-6 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            data-testid="prev-video"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const idx = VIDEOS.findIndex(v => v.id === fullscreen.id);
              setFullscreen(VIDEOS[(idx + 1) % VIDEOS.length]);
            }}
            className="absolute right-3 sm:right-6 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            data-testid="next-video"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Video */}
          <div className="w-full max-w-4xl px-12 sm:px-16" onClick={(e) => e.stopPropagation()}>
            <video
              key={fullscreen.id}
              ref={fullscreenRef}
              src={fullscreen.src}
              poster={fullscreen.poster}
              controls
              autoPlay
              playsInline
              preload="none"
              className="w-full rounded-xl shadow-2xl"
              style={{ maxHeight: '70vh' }}
              onError={(e) => {
                // Graceful fallback: replace failed video element with the poster image
                const img = document.createElement("img");
                img.src = fullscreen.poster;
                img.alt = t(fullscreen.titleKey);
                img.className = "w-full rounded-xl shadow-2xl";
                img.style.maxHeight = "70vh";
                e.currentTarget.replaceWith(img);
              }}
            />
            <div className="text-center mt-5">
              <h3 className="text-white font-bold text-lg">{t(fullscreen.titleKey)}</h3>
              <p className="text-slate-500 text-sm mt-1">{t(fullscreen.descKey)}</p>
              <p className="text-slate-700 text-xs mt-2">
                {VIDEOS.findIndex(v => v.id === fullscreen.id) + 1} / {VIDEOS.length} &middot; {t("video_showcase.nav_hint")}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoShowcaseSection;
